// The METRONOME as a mixer channel (#412): which voice clicks, which beats
// sound, what fills the beat, and whether the click goes through the master
// effects. The pure half lives here; the player draws the voices.

export type ClickSound = 'beep' | 'woodblock' | 'rim' | 'cowbell' | 'hihat';
/** Which beats of the bar click: every beat (the one accented), 2 and 4, or the one alone. */
export type ClickAccents = 'all' | 'two-four' | 'one';
export type ClickSubdivision = 'none' | 'eighths' | 'swing' | 'triplets' | 'sixteenths';
/** `post` runs the click through reverb and compression; `pre` goes straight to the output. */
export type ClickRouting = 'pre' | 'post';

export interface MetronomeSettings {
  sound: ClickSound;
  accents: ClickAccents;
  subdivision: ClickSubdivision;
  routing: ClickRouting;
}

export const DEFAULT_METRONOME: MetronomeSettings = {
  sound: 'beep',
  accents: 'all',
  subdivision: 'none',
  routing: 'pre',
};

export const CLICK_SOUNDS: ClickSound[] = ['beep', 'woodblock', 'rim', 'cowbell', 'hihat'];
export const CLICK_ACCENTS: ClickAccents[] = ['all', 'two-four', 'one'];
export const CLICK_SUBDIVISIONS: ClickSubdivision[] = [
  'none',
  'eighths',
  'swing',
  'triplets',
  'sixteenths',
];
export const CLICK_ROUTINGS: ClickRouting[] = ['pre', 'post'];

/** A persisted blob is untrusted: every field falls back to its default. */
export function normalizeMetronome(input: unknown): MetronomeSettings {
  const raw = (input ?? {}) as Partial<Record<keyof MetronomeSettings, unknown>>;
  const pick = <T>(value: unknown, allowed: readonly T[], fallback: T): T =>
    (allowed as readonly unknown[]).includes(value) ? (value as T) : fallback;
  return {
    sound: pick(raw.sound, CLICK_SOUNDS, DEFAULT_METRONOME.sound),
    accents: pick(raw.accents, CLICK_ACCENTS, DEFAULT_METRONOME.accents),
    subdivision: pick(raw.subdivision, CLICK_SUBDIVISIONS, DEFAULT_METRONOME.subdivision),
    routing: pick(raw.routing, CLICK_ROUTINGS, DEFAULT_METRONOME.routing),
  };
}

export type ClickKind = 'accent' | 'beat' | 'sub';

/**
 * What a beat of the bar clicks under the accent pattern, or null for none.
 * "2 and 4" is the jazz click — every other beat from the second, so a waltz
 * clicks its 2 and a 6/4 bar its 2, 4 and 6.
 */
export function clickFor(inBar: number, accents: ClickAccents): ClickKind | null {
  const beat = Math.max(0, Math.round(inBar));
  if (accents === 'one') return beat === 0 ? 'accent' : null;
  if (accents === 'two-four') return beat % 2 === 1 ? 'beat' : null;
  return beat === 0 ? 'accent' : 'beat';
}

/** Where the subdivision clicks fall inside a beat (fractions of the beat). */
export function subdivisionOffsets(subdivision: ClickSubdivision): number[] {
  switch (subdivision) {
    case 'eighths':
      return [0.5];
    case 'swing':
      return [2 / 3];
    case 'triplets':
      return [1 / 3, 2 / 3];
    case 'sixteenths':
      return [0.25, 0.5, 0.75];
    default:
      return [];
  }
}

/**
 * Whether bar `bar` (0-based from the first downbeat) clicks under a gap
 * click: `on` bars clicking, then `off` bars of silence, round again.
 */
export function gapClickSounds(bar: number, gap: { on: number; off: number } | undefined): boolean {
  if (!gap) return true;
  const on = Math.max(1, Math.round(gap.on));
  const off = Math.max(0, Math.round(gap.off));
  if (off === 0) return true;
  const at = ((Math.floor(bar) % (on + off)) + on + off) % (on + off);
  return at < on;
}
